import React from 'react'
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, IconButton, LinearProgress, MenuItem, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { ApplicationIcon, APPLICATION_ICON_OPTIONS } from '../../components/applications/ApplicationIcon'
import { AnimatedSection, AnimatedTableRow } from '../../components/animations/MotionContainers'
import { API_REQUESTS_PAUSED } from '../../data'
import { createApplication, deleteApplication, getApplications, updateApplication } from '../../services/admin/applicationsApi'
import { useAuth } from '../../context/AuthContext'
import { getAdminApplicationCode, tienePermiso } from '../../utils/permisos'
import { formatDateTime, getApiErrorMessage, getPaginatedItems } from '../../utils/apiData'
import { getApplicationImageUrl } from '../../utils/applicationImage'

const emptyForm = { code: '', name: '', description: '', url: '', icon: '', displayOrder: '0', active: true }

const normalizeApplication = (application) => ({
  id: application.id,
  code: application.code || '-',
  name: application.name || 'Aplicación sin nombre',
  description: application.description || '',
  url: application.url || '',
  imageUrl: getApplicationImageUrl(application.image_url || application.imageUrl),
  icon: application.icon || '',
  displayOrder: application.display_order ?? application.displayOrder ?? 0,
  active: application.active !== false,
  createdAt: application.created_at || application.createdAt,
  updatedAt: application.updated_at || application.updatedAt,
})

export const AdminApplications = () => {
  const { session, cargarSesion } = useAuth()
  const adminCode = getAdminApplicationCode(session)
  const canManage = tienePermiso(session, adminCode, 'APPLICATIONS_WRITE')
  const [applications, setApplications] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')
  const [successMessage, setSuccessMessage] = React.useState('')
  const [formOpen, setFormOpen] = React.useState(false)
  const [editing, setEditing] = React.useState(null)
  const [deleting, setDeleting] = React.useState(null)
  const [form, setForm] = React.useState(emptyForm)
  const [formError, setFormError] = React.useState('')
  const [logoFile, setLogoFile] = React.useState(null)
  const [removeImage, setRemoveImage] = React.useState(false)
  const [saving, setSaving] = React.useState(false)

  const loadApplications = React.useCallback(async () => {
    setLoading(true)
    setErrorMessage('')
    try {
      const response = await getApplications({ page: 1, limit: 100, sortDirection: 'asc' })
      setApplications(getPaginatedItems(response).map(normalizeApplication))
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, 'No se han podido cargar las aplicaciones.'))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    if (API_REQUESTS_PAUSED) return undefined
    loadApplications()
  }, [loadApplications])

  const closeForm = () => {
    if (saving) return
    setFormOpen(false)
    setEditing(null)
    setForm(emptyForm)
    setFormError('')
    setLogoFile(null)
    setRemoveImage(false)
  }

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setFormError('')
    setLogoFile(null)
    setRemoveImage(false)
    setFormOpen(true)
  }

  const openEdit = (application) => {
    setEditing(application)
    setForm({ code: application.code, name: application.name, description: application.description, url: application.url, icon: application.icon, displayOrder: String(application.displayOrder), active: application.active })
    setFormError('')
    setLogoFile(null)
    setRemoveImage(false)
    setFormOpen(true)
  }

  const handleSave = async (event) => {
    event.preventDefault()
    if (!canManage || saving) return
    setFormError('')
    setSuccessMessage('')
    setSaving(true)
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        description: form.description.trim(),
        icon: form.icon,
        ...(editing || form.url.trim() ? { url: form.url.trim() } : {}),
        displayOrder: Number(form.displayOrder),
        active: form.active,
        ...(logoFile ? { image: logoFile } : {}),
        ...(editing && removeImage ? { removeImage: true } : {}),
      }
      if (editing) await updateApplication(editing.id, payload)
      else await createApplication(payload)
      setFormOpen(false)
      setEditing(null)
      setForm(emptyForm)
      setLogoFile(null)
      setRemoveImage(false)
      setSuccessMessage(editing ? 'Aplicación actualizada correctamente.' : 'Aplicación creada correctamente.')
      await loadApplications()
      cargarSesion?.().catch(() => {})
    } catch (error) {
      setFormError(getApiErrorMessage(error, 'No se ha podido guardar la aplicación.'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!canManage || !deleting || saving) return
    setSaving(true)
    setErrorMessage('')
    setSuccessMessage('')
    try {
      await deleteApplication(deleting.id)
      setDeleting(null)
      setSuccessMessage('Aplicación eliminada correctamente.')
      await loadApplications()
      cargarSesion?.().catch(() => {})
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, 'No se ha podido eliminar la aplicación. Comprueba si tiene usuarios, roles o permisos asociados.'))
      setDeleting(null)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box className="page-fade">
      <Box className="page-heading">
        <Box>
          <Typography variant="h4">Aplicaciones</Typography>
          <Typography>Consulta las aplicaciones registradas en la suite, su estado, ruta de acceso y configuración general.</Typography>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
            Nueva aplicación
          </Button>
        )}
      </Box>

      <AnimatedSection><Box className="dashboard-section-card admin-panel-card admin-page-section">
        <Typography className="section-title">Aplicaciones configuradas</Typography>
        {loading && <LinearProgress sx={{ mb: 1.5 }} />}
        {errorMessage && <Alert severity="warning" sx={{ mb: 1.5 }}>{errorMessage}</Alert>}
        {successMessage && <Alert severity="success" onClose={() => setSuccessMessage('')} sx={{ mb: 1.5 }}>{successMessage}</Alert>}
        <Box className="activity-table-wrap">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Aplicación</TableCell>
                <TableCell>Código</TableCell>
                <TableCell>Ruta</TableCell>
                <TableCell align="right">Orden</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Creada</TableCell>
                <TableCell>Actualizada</TableCell>
                {canManage && <TableCell align="right">Acciones</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {applications.map((application) => (
                <TableRow component={AnimatedTableRow} key={application.id || application.code} hover>
                  <TableCell>
                    <Box className="activity-app-cell">
                      <Box className="activity-row-icon"><ApplicationIcon application={application} /></Box>
                      <Box>
                        <Typography>{application.name}</Typography>
                        <Typography className="admin-table-subtext">{application.description || 'Sin descripción disponible.'}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{application.code}</TableCell>
                  <TableCell>{application.url || 'Sin ruta'}</TableCell>
                  <TableCell align="right">{application.displayOrder}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      className={`admin-status-chip ${application.active ? 'admin-status-success' : 'suite-status-chip-muted'}`}
                      label={application.active ? 'Activa' : 'Inactiva'}
                    />
                  </TableCell>
                  <TableCell>{formatDateTime(application.createdAt, 'Sin fecha')}</TableCell>
                  <TableCell>{formatDateTime(application.updatedAt, 'Sin fecha')}</TableCell>
                  {canManage && <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Editar aplicación"><IconButton size="small" aria-label={`Editar ${application.name}`} onClick={() => openEdit(application)}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Eliminar aplicación"><IconButton size="small" color="error" aria-label={`Eliminar ${application.name}`} onClick={() => setDeleting(application)}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>}
                </TableRow>
              ))}
              {!loading && applications.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canManage ? 8 : 7}>
                    <Typography className="admin-user-empty">No hay aplicaciones configuradas.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box></AnimatedSection>
      <Dialog open={formOpen && canManage} onClose={closeForm} maxWidth="sm" fullWidth>
        <Box component="form" onSubmit={handleSave}>
          <DialogTitle>{editing ? 'Editar aplicación' : 'Nueva aplicación'}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {formError && <Alert severity="error">{formError}</Alert>}
              <TextField label="Código" value={form.code} required autoFocus slotProps={{ htmlInput: { maxLength: 80, pattern: '[A-Za-z0-9_]+' } }}
                onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })} />
              <TextField label="Nombre" value={form.name} required slotProps={{ htmlInput: { maxLength: 150 } }}
                onChange={(event) => setForm({ ...form, name: event.target.value })} />
              <TextField label="Descripción" value={form.description} multiline minRows={2}
                onChange={(event) => setForm({ ...form, description: event.target.value })} />
              <TextField label="URL" value={form.url} slotProps={{ htmlInput: { maxLength: 500 } }}
                onChange={(event) => setForm({ ...form, url: event.target.value })} />
              <TextField select label="Icono" value={form.icon} onChange={(event) => setForm({ ...form, icon: event.target.value })}>
                <MenuItem value="">Automatico</MenuItem>
                {APPLICATION_ICON_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <ApplicationIcon application={{ icon: option.value }} size={20} />
                      <span>{option.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                {editing?.imageUrl && !removeImage && !logoFile && <img src={editing.imageUrl} alt="Logo actual" style={{ width: 48, height: 48, objectFit: 'contain' }} />}
                <Button component="label" variant="outlined" disabled={saving}>
                  {logoFile ? 'Cambiar logo' : 'Seleccionar logo'}
                  <input hidden type="file" accept="image/png,image/jpeg" onChange={(event) => {
                    const file = event.target.files?.[0]
                    if (!file) return
                    setLogoFile(file)
                    setRemoveImage(false)
                    setFormError('')
                  }} />
                </Button>
                {logoFile && <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{logoFile.name}</Typography>}
                {(logoFile || (editing?.imageUrl && !removeImage)) && <Tooltip title="Quitar logo"><IconButton aria-label="Quitar logo" onClick={() => { setLogoFile(null); setRemoveImage(Boolean(editing?.imageUrl)) }} disabled={saving}><DeleteOutlineRoundedIcon /></IconButton></Tooltip>}
              </Stack>
              <TextField label="Orden" value={form.displayOrder} type="number" slotProps={{ htmlInput: { step: 1 } }}
                onChange={(event) => setForm({ ...form, displayOrder: event.target.value })} />
              <FormControlLabel control={<Switch checked={form.active} onChange={(event) => setForm({ ...form, active: event.target.checked })} />} label="Activa" />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeForm} disabled={saving}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={saving || !form.code.trim() || !form.name.trim() || form.displayOrder === ''}>
              {saving ? 'Guardando...' : editing ? 'Guardar cambios' : 'Crear aplicación'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Dialog open={Boolean(deleting) && canManage} onClose={() => !saving && setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar aplicación</DialogTitle>
        <DialogContent><DialogContentText>¿Eliminar {deleting?.name}? Esta acción no se puede deshacer.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={saving}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={saving}>{saving ? 'Eliminando...' : 'Eliminar'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
